import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  Layers,
  LayoutGrid,
  Phone,
  Home,
  ShoppingCart,
  Calendar,
  Package,
  Shield,
  Award,
  Video,
  Camera,
  CheckCircle,
  Home as HomeIcon,
  Users,
  Box,
} from 'lucide-react';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import { openCustomerPortal, openChat, PORTAL_BASE_URL } from '../../utils/cn';

// ─── Shared scroll-in motion variants (matches homepage vocabulary) ─
// Same EASE_OUT_EXPO curve used site-wide. Each section wraps in
// motion.section initial="hidden" whileInView="visible" so children
// inherit the state via variants propagation.

const sectionV = { hidden: {}, visible: {} };

const eyebrowV = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

const headlineV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: [0.16, 1, 0.3, 1] } },
};

const subheadV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.18, ease: [0.16, 1, 0.3, 1] } },
};

const ctaV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.36, ease: [0.16, 1, 0.3, 1] } },
};

// Card stagger — uses custom={i} to drive per-card delay.
const cardStaggerV = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.28 + i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

// Icon badge spring-pop for cards with cyan tile icons.
const iconBadgeV = {
  hidden: { scale: 0 },
  visible: (i) => ({
    scale: 1,
    transition: {
      delay: 0.42 + i * 0.08,
      type: 'spring',
      stiffness: 360,
      damping: 18,
    },
  }),
};

// Step entrance — scroll-fade-up for each <article id="step-N"> in
// the process section. Independent per step (each one gets its own
// viewport observer in its motion.article wrapper).
const stepV = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};

// Phase header (the colored gradient strip between step groups) —
// slides in from the left as you scroll into that phase.
const phaseHeaderV = {
  hidden: { opacity: 0, x: -16 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

// ──────────────────────────────────────────────────────────────────────
// /how-it-works — rebuild pass 1 (2026-05-24)
//
// Pass 1 = structure + section layout + real copy from the mockup.
// No scroll animations, no sticky rail, no FAQ accordion JS — those land
// in later passes per Mike's "structure first" directive.
//
// PRIOR VERSION preserved on disk as `index.v1-legacy.jsx` (multi-component
// scrollytelling build). To roll back: `git mv index.v1-legacy.jsx index.jsx`.
//
// HERO BG IMAGE: `/assets/images/iStock-2170456340-1770568303182.jpg`
// (carried over from the prior how-it-works hero — same asset).
//
// ─── BRAND TOKEN MAPPING from mockup → real OpenbooQ ────────────────
// Mockup used --blue #1ba3f0 / --ink #0a1628. Real codebase tokens:
//   #07B0E9   primary brand blue (used everywhere on the site)
//   #000150   headline navy
//   #0a1628   --ink (same as mockup's --ink)
//   #6cc7ff   --blue-glow (mockup-equivalent)
// All "blue" references in the mockup CSS are remapped to #07B0E9 below.
//
// ─── CLAIMS FROM MOCKUP — flag list for Mike's verification ─────────
// The mockup ships specific numeric/operational claims we should double-
// check. Carried verbatim into pass 1, but worth confirming before push:
//   • "Average 7 days end-to-end / max 10 days" — install timeline
//   • "~30 minutes to your line-itemed quote" — quote-turnaround
//   • "$2M general liability insurance per job" — insurance amount
//   • "Decking $42/board OpenbooQ cost vs $80-100 elsewhere" — pricing
//   • Enhancify financing, credit floor 550 — partner detail
//   • Kettering, OH HQ — may conflict with the homepage JSON-LD which
//     uses "1 E 4th St, Dayton, OH 45402" as the localBusiness address
//   • "50+ years of combined residential roofing experience" — founders
//   • "TAMKO Pro Platinum certified" — certification claim
//   • "GAF QuickMeasure" — specific tool name
//   • Phone 937-910-8791 — sourced from mockup; if call routing
//     differs from production, swap before push

const HOWTO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How OpenbooQ Works: From Address to New Roof in 7 Days',
  description:
    'The OpenbooQ process for replacing a residential roof online — from address entry through final inspection. Average 7 days end-to-end, maximum 10 days weather permitting. $0 down at signing.',
  totalTime: 'P7D',
  estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Enter Your Address', text: "Type your home address. OpenbooQ uses GAF QuickMeasure aerial imagery to map your roof's exact dimensions, pitch, and complexity — no in-person measurement visit required. You receive your full line-itemed quote in approximately 30 minutes." },
    { '@type': 'HowToStep', position: 2, name: 'Choose How Your Roof Is Designed', text: "Three ways to pick your new shingle: OpenbooQ's proprietary SmartPick AI uses color-matching technology to recommend a shingle that complements your home; Design Studio lets you browse the full GAF, Owens Corning, TAMKO, and Atlas catalogs; or call to speak with a real OpenbooQ roofer." },
    { '@type': 'HowToStep', position: 3, name: 'Exact Measurements and Final Price Confirmation', text: 'Before any contract is signed, OpenbooQ uses GAF QuickMeasure aerial imagery to verify exact roof dimensions. Your final, line-itemed price is locked in before you sign.' },
    { '@type': 'HowToStep', position: 4, name: 'Itemized Contract and Signing', text: 'A fully itemized contract with every material, labor component, and the 20% Service and Warranty fee listed. Sign online. No payment, no deposit, no card at signing. 72-hour free cancellation window.' },
    { '@type': 'HowToStep', position: 5, name: 'Materials, Scheduling and Crew Logistics', text: 'OpenbooQ assigns your dedicated project manager, locks in your install date, and dispatches your manufacturer-certified install crew. Every job is covered by $2 million in general liability insurance per job. Materials delivered the night before install.' },
    { '@type': 'HowToStep', position: 6, name: 'Installation and QC Walkthrough', text: 'Manufacturer-certified crews install per all building codes with body-cam recording on every install. A final inspection closes out the project. You walk the finished roof and sign off on the work.' },
    { '@type': 'HowToStep', position: 7, name: 'Payment and Warranty Activation', text: '$0 until your roof is complete. Payment is due at roof completion — not at signing, not during install, not until you have approved the finished work. Once payment clears, the 5-year OpenbooQ workmanship warranty activates and the manufacturer material warranty registers automatically.' },
  ],
};

// ─── Step content (rendered into the Process section below) ─────────
const PROCESS_STEPS = [
  {
    n: 1,
    title: 'Enter your address.',
    badge: 'You',
    time: '~1 min active · ~30 min for the report',
    lead: (
      <>
        Type your home address. OpenbooQ uses{' '}
        <strong>GAF QuickMeasure aerial imagery</strong> — the
        industry-standard tool used by professional roofing estimators —
        to map your roof's exact dimensions, pitch, and complexity. The
        measurement report takes about 30 minutes to come back.{' '}
        <strong>You can walk away while it runs.</strong>
      </>
    ),
    bullets: [
      <><strong>GAF QuickMeasure aerial imagery.</strong> Same measurement product professional estimators use. Accurate to within inches across every plane, edge, slope, and feature.</>,
      <><strong>No commitment, no payment, no sales call.</strong> Just type the address. Everything else is optional.</>,
      <><strong>Your full line-itemed quote arrives by email in ~30 minutes</strong> — or sooner, depending on how complex your roof is.</>,
    ],
    trust: (
      <><strong>Address to first quote in about the time it takes a traditional roofer to drive to your house</strong> — and you didn't have to stand in your driveway waiting for them.</>
    ),
  },
  {
    n: 2,
    title: 'Choose how your roof is designed.',
    badge: 'You',
    time: '~5–15 minutes',
    lead: 'Three ways to pick your new shingle. All online, all on your time, all no-pressure.',
    subGrid: [
      { Icon: Layers, title: 'SmartPick™ AI', body: "OpenbooQ's proprietary color-matching AI reads your existing roof color and recommends shingles that complement your home. Patent pending. No other roofer does this." },
      { Icon: LayoutGrid, title: 'Design Studio', body: 'Browse the full GAF Timberline HDZ, Owens Corning Oakridge & Duration, TAMKO Titan XT, and Atlas Pinnacle Pristine catalogs. Filter by color, style, warranty term.' },
      { Icon: Phone, title: 'Talk to a Roofer', body: "Call OpenbooQ at 937-910-8791 and get a real roofer on the line — not a commissioned salesperson. We're roofers first, sales pitch never." },
      { Icon: Home, title: 'Four top manufacturers', body: 'OpenbooQ installs from GAF, Owens Corning, TAMKO (Pro Platinum certified), and Atlas. More brand choice than at most local roofers.' },
    ],
    trust: (
      <><strong>SmartPick is OpenbooQ proprietary technology.</strong> We built it because picking a shingle from a paper sample book is the wrong way to choose what your house will look like for the next 30 years.</>
    ),
  },
  {
    n: 3,
    title: 'Exact measurements & final price confirmed.',
    badge: 'You',
    time: 'Automatic · under 1 minute',
    lead: (
      <>Your line-itemed price is verified and locked in <strong>before any contract is presented.</strong> The number you see is the number you sign.</>
    ),
    bullets: [
      <><strong>GAF QuickMeasure verifies every plane, edge, slope, and feature.</strong> Measurements accurate to within inches.</>,
      <><strong>Final price reflects your specific roof.</strong> Square footage, pitch, complexity, accessory products — all priced exactly.</>,
      <><strong>No change orders, no surprise costs.</strong> The only exception: unforeseen rotted decking, passed through at-cost with zero markup.</>,
    ],
    trust: (
      <><strong>Most roofers won't give you a final price</strong> until they've spent 90 minutes on your kitchen counter. We give you the final price before any contract appears.</>
    ),
  },
  {
    n: 4,
    title: 'Sign your itemized contract.',
    badge: 'You',
    time: '~5 minutes',
    lead: (
      <>Every cost line-itemed. <strong>No fine print, no fees added at signing, no card required.</strong> Sign online when you're ready.</>
    ),
    bullets: [
      <><strong>Every material, labor component, and the 20% Service & Warranty fee</strong> listed with prices.</>,
      <><strong>0% markup on materials, 0% markup on labor.</strong> We earn the 20% Service & Warranty fee. Nothing else.</>,
      <><strong>72-hour free cancellation window.</strong> Change your mind in the first 3 days, owe nothing.</>,
      <><strong>$0 at signing.</strong> No payment, no deposit, no card. Sign and walk away.</>,
    ],
    trust: (
      <><strong>This is the lowest-friction signing moment in residential roofing.</strong> By the end of this step, you've made a decision — but you've put zero dollars at risk and you still have 72 hours to change your mind.</>
    ),
  },
  {
    n: 5,
    title: 'Materials, scheduling & crew logistics.',
    badge: 'OpenbooQ',
    badgeColor: '#07B0E9',
    time: '~5 days · You do nothing',
    lead: (
      <>Once your contract is signed, OpenbooQ takes over. <strong>Materials ordered automatically</strong> from the manufacturer. <strong>Crew scheduled around our existing project queue</strong> for the soonest available install day. <strong>Materials dropped at your home the night before</strong> — staged, secured, ready for the crew. You don't need to be home, don't need to coordinate, don't need to follow up.</>
    ),
    subGrid: [
      { Icon: ShoppingCart, title: 'Materials Ordered Automatically', body: 'Direct from GAF, Owens Corning, TAMKO, or Atlas the moment your contract clears. No markup. No middleman. No phone calls required.' },
      { Icon: Calendar, title: 'Crew & Install Date Scheduled', body: 'Your dedicated project manager assigns the right crew and locks in your install date based on our current pipeline and weather forecasts.' },
      { Icon: Package, title: 'Materials Drop Night Before', body: 'All shingles, accessories, and supplies staged on your property the evening before install. Secured. Tarped if needed. Ready to roll at sunrise.' },
      { Icon: Shield, title: '$2M General Liability Insurance', body: 'OpenbooQ carries $2 million in general liability coverage per job — protecting your home through every phase from materials drop to final cleanup.' },
    ],
    trust: (
      <><strong>You'll hear from us once during this phase:</strong> to confirm your install day. Otherwise, we just handle it. No status calls. No coordination required. The reason it's "5 days" is because that's how long it takes us to fit you in — not because you have to do anything.</>
    ),
  },
  {
    n: 6,
    title: 'Installation & QC walkthrough.',
    badge: 'Install Day',
    badgeColor: '#10B981',
    time: '1–2 days for most homes',
    lead: (
      <>Manufacturer-certified crews install in <strong>1–2 days</strong> for most homes — depending on roof size and complexity. <strong>Body-cam recorded throughout.</strong> A final inspection closes out the install. <strong>You walk the finished roof and sign off on the work.</strong></>
    ),
    subGrid: [
      { Icon: Award, title: 'Certified Installation', body: 'Manufacturer-certified crew led by a vetted install lead. Dedicated OpenbooQ project manager on-site or on-call from tear-off through cleanup.' },
      { Icon: Video, title: 'Body-Cam on Every Install', body: 'Crew leads wear body cams for the entire job. Footage available to you on request. Standard on every install — not optional, not aspirational.' },
      { Icon: Camera, title: 'Real-Time Photo Documentation', body: 'Photos at each install phase — tear-off, decking inspection, underlayment, shingle install, ridge, cleanup — pushed to your project dashboard.' },
      { Icon: CheckCircle, title: 'Final Quality Inspection', body: 'Independent QC walkthrough at the end of the install. You walk the roof with us — literally or virtually. Your sign-off triggers your final invoice — which is the next and final step.' },
    ],
    trust: (
      <><strong>Once you sign off on the QC walkthrough, your install is officially complete.</strong> From address entry to finished roof: average 7 days. Maximum 10, weather permitting.</>
    ),
  },
  {
    n: 7,
    title: '$0 until your roof is complete.',
    badge: '$0 Down',
    time: 'Payment due at completion',
    lead: (
      <><strong>This is the final step — and the first moment a dollar changes hands.</strong> Payment is due at roof completion. Not at signing. Not during install. Not until you've walked your finished roof and signed off on the work. <strong>Then — and only then — your final invoice is issued.</strong></>
    ),
    paymentTimeline: true,
    trust: (
      <><strong>OpenbooQ doesn't see a dollar until your roof is on and approved.</strong> When payment clears, your 5-year workmanship warranty activates and your manufacturer material warranty (GAF / Owens Corning / TAMKO / Atlas) registers automatically. The risk lives on our side of the table, not yours.</>
    ),
  },
];

// ─── FAQ items (rendered into the FAQ section below) ────────────────
const FAQ_ITEMS = [
  {
    q: 'Why are you so cheap?',
    a: (
      <>
        <p>We're not cheap — traditional roofers are expensive. Most local roofers add <strong>80%+ markup on materials</strong>, <strong>10–15% sales commissions</strong> on every job, plus inflated overhead from in-home appointments and aggressive marketing.</p>
        <p>OpenbooQ removes all of that. <strong>Materials and labor pass through at cost.</strong> Our entire margin is the 20% flat Service & Warranty fee — which covers your warranty, project management, QC, and $2M insurance. Same shingles. Same crews. 30–50% less.</p>
      </>
    ),
  },
  {
    q: 'Who actually does the work on my roof?',
    a: (
      <>
        <p><strong>Manufacturer-certified install crews</strong> — GAF, Owens Corning, TAMKO, and Atlas certified for the products they're installing. Every crew operates under OpenbooQ's standardized installation protocols.</p>
        <p>Every install includes <strong>body-cam recording</strong> from tear-off through cleanup, and a <strong>dedicated OpenbooQ project manager</strong> assigned to your specific job. Not subcontractors-of-subcontractors. Real crews, real accountability, real footage available on request.</p>
      </>
    ),
  },
  {
    q: 'What kind of warranty comes with an OpenbooQ roof?',
    a: (
      <>
        <p>Two warranty layers, stacked:</p>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li><strong>5-year OpenbooQ workmanship warranty</strong> — covers the install itself: labor, leak protection, and install-related water damage.</li>
          <li><strong>Manufacturer material warranty</strong> — direct from GAF, Owens Corning, TAMKO, or Atlas. Coverage depends on the shingle line you choose, up to lifetime non-prorated.</li>
        </ul>
        <p>Both register automatically when your final payment clears. One call to OpenbooQ handles either claim. For TAMKO Titan XT installs, OpenbooQ is <strong>Pro Platinum certified</strong> — the top installer tier — which unlocks extended warranty programs.</p>
      </>
    ),
  },
  {
    q: 'Can the price change after I sign?',
    a: (
      <>
        <p><strong>Your itemized contract price is locked.</strong> The only exception is undisclosed structural damage discovered during tear-off — rotted decking or framing issues that weren't visible from the surface before the old roof came off.</p>
        <p>If that happens: we stop work, document the issue with photos, and <strong>you approve the at-cost replacement quote before any additional work happens.</strong> Decking runs <strong>$42 per board at our cost</strong> (vs. $80–$100 elsewhere). Zero markup. OpenbooQ profits nothing on decking work, so we only replace what genuinely needs replacing.</p>
      </>
    ),
  },
  {
    q: 'Do you do insurance work?',
    a: (
      <>
        <p>We don't work directly with insurance adjusters, but we strongly encourage you to <strong>get your OpenbooQ quote before you file your claim.</strong> Two reasons it helps:</p>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li>Adjusters often use the same Xactimate / QuickMeasure-style pricing methodology we do, so the math reconciles cleanly.</li>
          <li>Filing your claim with our transparent, itemized quote in hand means your carrier sees the actual cost of the work from the start — which can help speed up the process.</li>
        </ul>
        <p>If you have storm or hail damage, <strong>get an OpenbooQ quote first, then file your claim and share the quote with your carrier at the time of filing</strong> (along with photos of the damage). The claim covers the real repair cost, and your insurance company settles directly with you.</p>
      </>
    ),
  },
  {
    q: 'How do I know OpenbooQ is real? This sounds too good to be true.',
    a: (
      <>
        <p>Fair question. Here's what's verifiable:</p>
        <ul className="list-disc pl-6 space-y-2 mb-4">
          <li><strong>32 verified 5-star Google reviews</strong> from completed Dayton and Cincinnati installs (and counting)</li>
          <li><strong>Headquartered in Kettering, OH</strong> — our HQ is on Google Maps, no hiding</li>
          <li><strong>50+ years of combined residential roofing experience</strong> among the founding team</li>
          <li><strong>$2M general liability insurance per job</strong> — double the typical industry coverage</li>
          <li><strong>Manufacturer certifications</strong> including TAMKO Pro Platinum (top installer tier)</li>
        </ul>
        <p>We were founded by roofers who got tired of how the industry treats homeowners — not by tech founders who wandered into the category. Want to meet the team? Call 937-910-8791. <strong>A real roofer picks up.</strong></p>
      </>
    ),
  },
];

const FAQPAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(({ q }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: 'See https://openbooq.com/how-it-works for the full answer.' },
  })),
};

// ─── Eyebrow pill — matches the mockup's section-eyebrow pattern ─
//
// Renders the section-introducer label as a rounded pill with
// light-cyan bg + cyan text + a small dot. Light variant for white
// sections (default), dark variant for the dark hero/insurance bands.
const EyebrowPill = ({ children, variant = 'light' }) => {
  const styles =
    variant === 'dark'
      ? { background: 'rgba(7, 176, 233, 0.18)', border: '1px solid rgba(7, 176, 233, 0.45)', color: '#6cc7ff' }
      : { background: 'rgba(7, 176, 233, 0.10)', border: '1px solid rgba(7, 176, 233, 0.30)', color: '#0a6eb8' };
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
      style={styles}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ background: variant === 'dark' ? '#10B981' : '#07B0E9' }}
        aria-hidden="true"
      />
      {children}
    </div>
  );
};

// ─── Small reusable building blocks ──────────────────────────────────

const SectionHeader = ({ eyebrow, headline, subhead, dark = false, centered = true }) => (
  <header className={centered ? 'text-center mb-10 lg:mb-14' : 'mb-10 lg:mb-14'}>
    {eyebrow && (
      <div className="mb-4 flex justify-center">
        <EyebrowPill variant={dark ? 'dark' : 'light'}>{eyebrow}</EyebrowPill>
      </div>
    )}
    {headline && (
      <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
        style={{
          color: dark ? '#ffffff' : '#0a1628',
          fontFamily: "'Poppins', 'Montserrat', sans-serif",
          letterSpacing: '-0.025em',
        }}
      >
        {headline}
      </h2>
    )}
    {subhead && (
      <p
        className={[
          'text-base lg:text-lg max-w-3xl leading-relaxed',
          centered ? 'mx-auto' : '',
        ].join(' ')}
        style={{ color: dark ? 'rgba(255,255,255,0.78)' : '#6B7A8E' }}
      >
        {subhead}
      </p>
    )}
  </header>
);

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#07B0E9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Scroll-driven sticky rail with passed/active/upcoming states ──
//
// Uses IntersectionObserver per step <article id="step-N">. Whichever
// step is in the middle ~30-50% of the viewport sets `activeStep`.
// Dots before activeStep render as PASSED (filled blue), the active
// dot renders CYAN with its phase label visible, dots after render
// in default empty state. A cyan progress bar fills the rail track
// behind the dots, ending at the active dot's center.
const RAIL_DOTS = [
  { n: 1, label: 'Address' },
  { n: 2, label: 'Design' },
  { n: 3, label: 'Measure' },
  { n: 4, label: 'Sign' },
  { n: 5, label: 'Prep' },
  { n: 6, label: 'Install' },
  { n: 7, label: 'Pay' },
];

const useActiveStep = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio that's
        // intersecting; that's our current active step.
        let best = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!best || entry.intersectionRatio > best.intersectionRatio) {
              best = entry;
            }
          }
        });
        if (best) {
          const n = parseInt(best.target.id.replace('step-', ''), 10);
          if (!Number.isNaN(n)) setActiveStep(n);
        }
      },
      {
        // Trigger when step's top is ~25% from viewport top and bottom
        // is ~50% from viewport bottom — gives a reliable "this is the
        // step I'm reading right now" detection across screen heights.
        rootMargin: '-25% 0px -50% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    document
      .querySelectorAll('article[id^="step-"]')
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return activeStep;
};

const ProcessRail = ({ activeStep }) => {
  // Progress bar height — fill from top to active dot's center.
  // Each dot is 36px tall with 36px margin-bottom (line-height === dot
  // height makes spacing predictable). Progress fills (activeStep - 1)
  // full intervals + half the active dot when activeStep >= 1.
  const progressPct =
    activeStep <= 0
      ? 0
      : ((activeStep - 0.5) / (RAIL_DOTS.length - 0.5)) * 100;

  return (
    <aside
      className="hidden lg:block sticky self-start"
      // top:112px matches the mockup's .process-rail rule exactly.
      // align-self: start (via self-start) is required for sticky to
      // work inside a CSS grid — without it, the grid item stretches
      // to row height and sticky doesn't engage.
      style={{ top: '112px', height: 'max-content' }}
      aria-label="Process step navigation"
    >
      <div className="relative" style={{ padding: '0 18px' }}>
        {/* Track line — gray base */}
        <div
          className="absolute"
          aria-hidden="true"
          style={{
            left: '50%',
            top: '18px',
            bottom: '18px',
            width: '2px',
            background: '#E5EBF0',
            transform: 'translateX(-50%)',
            borderRadius: '1px',
            zIndex: 0,
          }}
        />
        {/* Cyan progress fill */}
        <div
          className="absolute"
          aria-hidden="true"
          style={{
            left: '50%',
            top: '18px',
            width: '2px',
            background: 'linear-gradient(180deg, #07B0E9 0%, #0a6eb8 100%)',
            transform: 'translateX(-50%)',
            height: `calc(${progressPct}% - 36px * ${progressPct / 100})`,
            transition: 'height 500ms cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1,
            borderRadius: '1px',
          }}
        />
        {/* Dots */}
        {RAIL_DOTS.map((dot, i, arr) => {
          const isPassed = dot.n < activeStep;
          const isActive = dot.n === activeStep;
          const dotStyle = isActive
            ? {
                background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                border: '2px solid #07B0E9',
                color: 'white',
                boxShadow: '0 6px 20px rgba(7, 176, 233, 0.45)',
                transform: 'scale(1.12)',
              }
            : isPassed
            ? {
                background: '#07B0E9',
                border: '2px solid #07B0E9',
                color: 'white',
              }
            : {
                background: 'white',
                border: '2px solid #E5EBF0',
                color: '#6B7A8E',
              };
          return (
            <a
              key={dot.n}
              href={`#step-${dot.n}`}
              className="relative flex items-center justify-center rounded-full font-black text-[13px] transition-all duration-300 group"
              style={{
                width: '36px',
                height: '36px',
                marginBottom: i === arr.length - 1 ? '0' : '36px',
                zIndex: 2,
                textDecoration: 'none',
                ...dotStyle,
              }}
              aria-label={`Jump to step ${dot.n} — ${dot.label}`}
              aria-current={isActive ? 'step' : undefined}
            >
              {dot.n}
              <span
                className="absolute pointer-events-none transition-opacity duration-300"
                style={{
                  left: 'calc(100% + 12px)',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: isActive ? '#0a6eb8' : '#6B7A8E',
                  whiteSpace: 'nowrap',
                  opacity: isActive ? 1 : 0,
                }}
              >
                {dot.label.toUpperCase()}
              </span>
            </a>
          );
        })}
      </div>
    </aside>
  );
};

// ─── Page ────────────────────────────────────────────────────────────

const HowItWorks = () => {
  const handleCtaClick = (e) => {
    e.preventDefault();
    openCustomerPortal();
  };

  // Open the global Chatbase widget — delegated to the shared util in
  // utils/cn.js so every "open chat" surface across the site routes
  // through one place.
  const handleOpenChat = (e) => {
    e.preventDefault();
    openChat();
  };

  // Scroll-driven rail state — which step is currently in view.
  const activeStep = useActiveStep();

  return (
    <>
      <Helmet>
        <title>How It Works — OpenbooQ · From Address to New Roof in 7 Days</title>
        <meta
          name="description"
          content="See exactly how OpenbooQ works. From the moment you enter your address to the day you walk your new roof — average 7 days, max 10. ~30 minutes to your line-itemed quote. $0 down. Serving Dayton & Cincinnati."
        />
        <script type="application/ld+json">{JSON.stringify(HOWTO_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQPAGE_SCHEMA)}</script>
      </Helmet>

      <Header />

      <main>
        {/* ═══ HERO ═══════════════════════════════════════════════════
            iStock home photo bg + dark navy overlay + blue grid pattern.
            Fits in a single viewport. "7" giant cyan on left, "DAYS"
            caps to its right baseline-aligned at the absolute bottom
            of the 7. Meta row at the bottom is a centered compact row
            (not full-width) — big cyan number per stat with a small
            white label below. */}
        <section
          className="relative overflow-hidden flex flex-col pt-48 sm:pt-32 lg:pt-[150px]"
          style={{
            minHeight: '100vh',
            paddingBottom: 'clamp(40px, 5vw, 64px)',
            backgroundImage: `url('/assets/images/iStock-2170456340-1770568303182.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundColor: '#0a1628',
          }}
        >
          {/* Dark navy overlay over the photo — keeps text legible and
              gives the cyan grid pattern + glow somewhere dark to read. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(10, 22, 40, 0.88) 0%, rgba(14, 30, 54, 0.82) 100%)',
            }}
          />
          {/* Cyan ambient glow center-top */}
          <div
            className="absolute pointer-events-none"
            aria-hidden="true"
            style={{
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '50%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          <motion.div
            className="relative max-w-[1080px] mx-auto px-5 lg:px-8 text-center text-white w-full flex flex-col items-center flex-1"
            initial="hidden"
            animate="visible"
            variants={sectionV}
          >
            {/* Eyebrow pill */}
            <motion.div variants={eyebrowV} className="mb-8 lg:mb-10 flex justify-center">
              <EyebrowPill variant="dark">The OpenbooQ Process</EyebrowPill>
            </motion.div>

            {/* Giant "7 DAYS" lockup — items-baseline aligns DAYS by
                its text baseline to the bottom of the giant 7 (per the
                mockup target). DAYS sits at the foot of the 7, not its
                middle. */}
            <motion.div
              variants={{
                hidden: { opacity: 0, scale: 0.88 },
                visible: { opacity: 1, scale: 1, transition: { duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] } },
              }}
              className="flex items-baseline justify-center gap-3 lg:gap-5 mb-6 lg:mb-8"
            >
              <span
                className="font-black leading-[0.85]"
                style={{
                  fontSize: 'clamp(120px, 18vw, 220px)',
                  background: 'linear-gradient(180deg, #6cc7ff 0%, #07B0E9 50%, #0a6eb8 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.04em',
                  filter: 'drop-shadow(0 8px 32px rgba(7,176,233,0.35))',
                }}
              >
                7
              </span>
              <span
                className="font-black uppercase leading-none"
                style={{
                  fontSize: 'clamp(36px, 5vw, 68px)',
                  color: 'white',
                  letterSpacing: '-0.01em',
                }}
              >
                Days
              </span>
            </motion.div>

            {/* Headline — max-width tuned so the natural English break
                lands at "end-/to-end" (the hyphenated word wraps at the
                first hyphen on narrow widths). */}
            <motion.h1
              variants={headlineV}
              className="font-black mb-5 leading-[1.1] tracking-tight mx-auto"
              style={{
                fontSize: 'clamp(28px, 3.6vw, 44px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
                maxWidth: '760px',
                color: 'white',
              }}
            >
              From your <span style={{ color: '#07B0E9' }}>address</span> to a new roof. Average <span style={{ color: '#07B0E9' }}>7 days</span> end-to-end.
            </motion.h1>

            <motion.p
              variants={subheadV}
              className="mb-6 lg:mb-8 max-w-3xl mx-auto leading-relaxed"
              style={{
                fontSize: 'clamp(14px, 1.2vw, 17px)',
                color: 'rgba(255,255,255,0.80)',
              }}
            >
              From the moment you enter your home address to the day you walk your finished roof.{' '}
              <strong style={{ color: '#ffffff', fontWeight: 800 }}>~30 minutes</strong>{' '}
              to your line-itemed quote.{' '}
              <strong style={{ color: '#ffffff', fontWeight: 800 }}>$0 down</strong>{' '}
              at signing. Maximum 10 days, weather permitting.
            </motion.p>

            {/* Scroll CTA — pill, more subtle than the gradient button */}
            <motion.a
              variants={ctaV}
              href="#process"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 mb-auto"
              style={{
                background: 'rgba(255, 255, 255, 0.08)',
                color: 'white',
                border: '1px solid rgba(108, 199, 255, 0.30)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
              }}
            >
              See the 7-step process
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </motion.a>

            {/* Meta row — SINGLE centered row on desktop with · dividers.
                Constrained to max-w-3xl (768px) — same width as the
                headline + paragraph above — so the 4 stats spread
                evenly within that contained width, NOT edge-to-edge
                across the full viewport. Previous unconstrained
                full-width version let the leftmost stat overflow and
                get clipped by body { overflow-x: clip }, which is why
                "$0 down at signing" appeared to be missing.
                Mobile (< md): 2×2 grid for clean stacking. Top
                hairline border separator stays full-width.
                Fades in last in the hero cascade. */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 8 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.50, ease: [0.16, 1, 0.3, 1] } },
              }}
              className="w-full pt-6 mt-10 lg:mt-12"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}
            >
              {(() => {
                const stats = [
                  { num: '$0', label: 'down at signing' },
                  { num: '~30 min', label: 'to your quote' },
                  { num: '72-hr', label: 'free cancellation' },
                  { num: '$2M', label: 'liability insurance per job' },
                ];
                return (
                  <>
                    {/* Desktop (md+): contained centered row, justify-between
                        spreads the 7 items (4 stats + 3 dividers) evenly
                        within the constrained width. */}
                    <div className="hidden md:flex items-baseline justify-between max-w-3xl mx-auto whitespace-nowrap">
                      {stats.map((stat, i) => (
                        <React.Fragment key={stat.label}>
                          <div className="flex items-baseline gap-1.5">
                            <span
                              className="font-black"
                              style={{
                                fontSize: 'clamp(16px, 1.4vw, 20px)',
                                color: '#6cc7ff',
                                letterSpacing: '-0.02em',
                              }}
                            >
                              {stat.num}
                            </span>
                            <span
                              className="text-xs lg:text-sm"
                              style={{ color: 'rgba(255, 255, 255, 0.70)' }}
                            >
                              {stat.label}
                            </span>
                          </div>
                          {i < stats.length - 1 && (
                            <span
                              aria-hidden="true"
                              style={{ color: 'rgba(255, 255, 255, 0.25)' }}
                            >
                              ·
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Mobile (< md): 2×2 grid — ensures all four stats
                        stay visible and balanced on phone widths. */}
                    <div className="grid md:hidden grid-cols-2 gap-y-4 gap-x-6 max-w-md mx-auto">
                      {stats.map((stat) => (
                        <div
                          key={stat.label}
                          className="flex flex-col items-center text-center"
                        >
                          <span
                            className="font-black"
                            style={{
                              fontSize: '20px',
                              color: '#6cc7ff',
                              letterSpacing: '-0.02em',
                            }}
                          >
                            {stat.num}
                          </span>
                          <span
                            className="text-[11px] mt-0.5"
                            style={{ color: 'rgba(255, 255, 255, 0.70)' }}
                          >
                            {stat.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ INTRO BAND + 3-phase strip ═══════════════════════════
            Constrained-width centered column matching the mockup:
            section + headline + paragraph + 3-card row all live inside
            a max-w-5xl (1024px) container, not the wider 1080px the
            page uses elsewhere. Headline wraps to 2 lines via an
            explicit max-w on the h2. Cards are clearly bordered with
            soft shadows; DAY 7 is the featured card with a cyan border
            + light blue tinted bg fill. */}
        <motion.section
          className="bg-white py-16 lg:py-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={sectionV}
        >
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <div className="text-center mb-10 lg:mb-12">
              <motion.div variants={eyebrowV} className="mb-5 flex justify-center">
                <EyebrowPill>How It Works</EyebrowPill>
              </motion.div>
              <motion.h2
                variants={headlineV}
                className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight mx-auto"
                style={{
                  color: '#0a1628',
                  fontFamily: "'Poppins', 'Montserrat', sans-serif",
                  letterSpacing: '-0.025em',
                  maxWidth: '720px',
                }}
              >
                Seven steps. <span style={{ color: '#07B0E9' }}>One week.</span> Five minutes of yours.
              </motion.h2>
              <motion.p variants={subheadV} className="text-base lg:text-lg text-gray-500 max-w-3xl mx-auto leading-relaxed">
                Type your address, pick your shingle, sign. Your hands-on time is about <strong>5 minutes</strong>. The 30-minute clock from address to a full quote is mostly GAF QuickMeasure crunching your exact roof dimensions — not you. From there, we handle materials, scheduling, and crew logistics. Your new roof goes on in <strong>1–2 days</strong>, usually within a week of signing.
              </motion.p>
            </div>

            {/* 3-card row — progressive tint left-to-right (white →
                faint cool → blue featured) builds toward the install-
                day payoff. Subtle horizontal gradient connector behind
                the cards shows only in the gaps (desktop). Cards have
                slightly squared corners (rounded-xl, 12px) and reduced
                stat-number size to match the mockup. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 relative">
              {/* Connector line — thin horizontal gradient behind the
                  cards. Cards have opaque/tinted bg so the line only
                  shows through the grid gaps. Desktop only; hidden when
                  cards stack vertically on mobile. */}
              <div
                className="hidden md:block absolute pointer-events-none"
                aria-hidden="true"
                style={{
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '2px',
                  background:
                    'linear-gradient(90deg, rgba(7, 176, 233, 0.10) 0%, rgba(7, 176, 233, 0.30) 60%, rgba(7, 176, 233, 0.55) 100%)',
                  transform: 'translateY(-50%)',
                  zIndex: 0,
                  borderRadius: '1px',
                }}
              />

              {/* Card backgrounds = the mockup's exact values (read from
                  its .phase-card / .phase-card.us / .phase-card.final
                  CSS rules):
                    Card 1 (you):     #ffffff
                    Card 2 (us):      #fafbfd  ← --paper-warm
                    Card 3 (final):   linear-gradient(180deg, #f0f9ff 0%, #ffffff 70%)
                  Border = #e5ebf0  ← --gray-border (from mockup), with
                  the featured card swapping in a 2px cyan border. */}
              {[
                { day: 'Day 1', time: '~5 min', title: 'You sign your roof', desc: 'Type your address (1 min). Pick your shingle and sign (~5 min). Most of the 30-minute clock is GAF QuickMeasure crunching your roof — not you.', actor: 'You', actorColor: '#0a6eb8', actorBg: '#e8f5fd', tint: 'none' },
                { day: 'Days 2–6', time: '~5 days', title: 'We get ready', desc: 'Materials ordered automatically. Crew scheduled around our queue. Materials delivered the night before install.', actor: 'OpenbooQ', actorColor: '#7c3aed', actorBg: '#ede9fe', tint: 'faint' },
                { day: 'Day 7', time: '1–2 days', title: 'Your roof goes on', desc: 'Install in 1–2 days for most homes. Body-cam recorded. Final QC. Payment due at completion.', actor: 'Install Day', actorColor: '#047857', actorBg: '#d1fae5', tint: 'featured' },
              ].map((phase, i) => {
                const cardBg =
                  phase.tint === 'featured'
                    ? 'linear-gradient(180deg, #f0f9ff 0%, #ffffff 70%)'
                    : phase.tint === 'faint'
                    ? '#fafbfd'
                    : '#ffffff';
                const cardBorder =
                  phase.tint === 'featured' ? '2px solid #07B0E9' : '1px solid #e5ebf0';
                const cardShadow =
                  phase.tint === 'featured'
                    ? '0 12px 32px rgba(7, 176, 233, 0.18), 0 0 0 4px rgba(7, 176, 233, 0.06)'
                    : '0 1px 3px rgba(15, 23, 42, 0.06), 0 8px 24px rgba(15, 23, 42, 0.08)';
                return (
                  <motion.article
                    key={phase.day}
                    variants={cardStaggerV}
                    custom={i}
                    whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                    className="rounded-xl p-6 lg:p-7 relative flex flex-col cursor-default"
                    style={{
                      background: cardBg,
                      border: cardBorder,
                      boxShadow: cardShadow,
                      zIndex: 1, // sit above the connector line
                    }}
                  >
                    <div
                      className="text-xs font-bold uppercase tracking-[0.14em] mb-3"
                      style={{ color: '#6B7A8E' }}
                    >
                      {phase.day}
                    </div>
                    {/* Time as the dominant cyan headline — sized down per mockup */}
                    <div
                      className="font-black leading-none mb-3"
                      style={{
                        fontSize: 'clamp(34px, 4.2vw, 48px)',
                        color: '#07B0E9',
                        letterSpacing: '-0.035em',
                      }}
                    >
                      {phase.time}
                    </div>
                    <h3 className="text-lg lg:text-xl font-bold mb-2 leading-snug" style={{ color: '#0a1628' }}>
                      {phase.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5 flex-1">{phase.desc}</p>
                    <span
                      className="inline-block self-start px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em]"
                      style={{
                        backgroundColor: phase.actorBg,
                        color: phase.actorColor,
                      }}
                    >
                      {phase.actor}
                    </span>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* ═══ PROCESS — sticky-rail layout (mockup-faithful) ═══════
            Layout is owned by the `.process-wrap` CSS class in
            tailwind.css (max-width 1180px centered + 88px/1fr grid on
            lg+ + 56px gap + align-items:start). Defined as a real CSS
            class because the equivalent Tailwind arbitrary value
            (`lg:grid-cols-[88px_1fr]`) was not reliably compiling.
            Mobile (< lg): rail aside is `hidden lg:block` so only
            content renders in a single column. */}
        <section id="process" className="py-16 lg:py-24 bg-white">
          <div className="process-wrap">

            {/* LEFT — scroll-driven sticky rail (lg+ only) */}
            <ProcessRail activeStep={activeStep} />

            {/* RIGHT — step content (flows in 1fr column) */}
            <div className="space-y-10 lg:space-y-14 min-w-0">

              {/* Phase 1 header — blue */}
              <PhaseHeader
                variant="you"
                eyebrow="Phase 1 · Day 1"
                title="Your 30 minutes online"
                meta="Steps 1–4 happen on your first visit."
              />

              {PROCESS_STEPS.slice(0, 4).map((step) => (
                <ProcessStep key={step.n} step={step} />
              ))}

              {/* Phase 2 header — purple */}
              <PhaseHeader
                variant="us"
                eyebrow="Phase 2 · Days 2–6"
                title="We take over"
                meta="Step 5 happens automatically. You do nothing."
              />

              <ProcessStep step={PROCESS_STEPS[4]} />

              {/* Phase 3 header — green */}
              <PhaseHeader
                variant="final"
                eyebrow="Phase 3 · Day 7"
                title="Install & complete"
                meta="Steps 6–7. New roof. Warranty active."
              />

              {PROCESS_STEPS.slice(5).map((step) => (
                <ProcessStep key={step.n} step={step} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══ QUALITY DEEP-DIVE ════════════════════════════════════ */}
        <motion.section
          className="bg-white py-16 lg:py-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={sectionV}
        >
          <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow="The Honest Answer"
              headline={<>Does a lower price mean lower quality? <span style={{ color: '#07B0E9' }}>No.</span></>}
              subhead="We didn't cut quality. We cut the parts of the traditional roofing model that never benefited the homeowner — the markups, the sales commissions, the inflated overhead. Here's exactly how the new math works."
            />

            {/* Pricing math — no outer container box per the mockup;
                eyebrow, headline, subhead, and 3-card row sit free on
                the section's white bg. */}
            <div className="mb-10 lg:mb-12">
              <div className="text-center mb-8 lg:mb-10">
                <div className="mb-4 flex justify-center">
                  <EyebrowPill>The Pricing Math</EyebrowPill>
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight mb-3 max-w-3xl mx-auto" style={{ color: '#0a1628', letterSpacing: '-0.025em', fontFamily: "'Poppins', 'Montserrat', sans-serif" }}>
                  The formula is simpler than the industry wants you to think.
                </h3>
                <p className="text-base text-gray-500 max-w-2xl mx-auto">
                  Most roofing quotes hide everything behind a single big number. <strong>OpenbooQ shows every line — and the entire formula has three parts. That's it.</strong>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                {[
                  { label: 'Materials', value: 'At Cost', markup: '+ 0% markup', markupColor: '#10B981', desc: 'GAF, Owens Corning, TAMKO, or Atlas. What we pay our suppliers is what you pay us.', highlight: false },
                  { label: 'Labor', value: 'At Cost', markup: '+ 0% markup', markupColor: '#10B981', desc: 'Direct crew rates. No retail markup. No salesperson commission baked in.', highlight: false },
                  { label: 'Service & Warranty Fee', value: '20%', markup: 'flat · our entire margin', markupColor: '#07B0E9', desc: 'Covers your 5-year workmanship warranty, project management, QC, $2M insurance, and customer service.', highlight: true },
                ].map((cell, i) => (
                  <motion.article
                    key={cell.label}
                    variants={cardStaggerV}
                    custom={i}
                    whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                    className="rounded-2xl p-6 bg-white text-center cursor-default"
                    style={{
                      boxShadow: cell.highlight
                        ? 'inset 0 0 0 2px rgba(7, 176, 233, 0.55), 0 12px 32px rgba(7, 176, 233, 0.18)'
                        : 'inset 0 0 0 1px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    <div className="text-xs font-bold uppercase tracking-[0.14em] mb-3" style={{ color: cell.highlight ? '#07B0E9' : '#6B7A8E' }}>
                      {cell.label}
                    </div>
                    <div
                      className="font-black mb-1 leading-none"
                      style={{
                        fontSize: 'clamp(40px, 4.5vw, 56px)',
                        color: '#07B0E9',
                        letterSpacing: '-0.035em',
                      }}
                    >
                      {cell.value}
                    </div>
                    <div className="text-xs font-bold mb-4 mt-1" style={{ color: cell.markupColor }}>
                      {cell.markup}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{cell.desc}</p>
                  </motion.article>
                ))}
              </div>

              {/* Bottom callout — white rounded box w/ light border,
                  cyan-bold "Every roof. Every quote. Every time." */}
              <div
                className="mt-8 lg:mt-10 rounded-2xl bg-white py-5 px-6 lg:py-6 lg:px-8 text-center text-base lg:text-lg"
                style={{
                  color: '#0a1628',
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                }}
              >
                That's the whole formula.{' '}
                <strong style={{ color: '#07B0E9' }}>Every roof. Every quote. Every time.</strong>{' '}
                No hidden markup, no surprise line items, no "oh and one more thing."
              </div>
            </div>

            {/* Quality pillars — now with cyan icon tiles per mockup */}
            <div className="text-center mb-8 lg:mb-10">
              <h3 className="text-2xl sm:text-3xl font-black mb-3" style={{ color: '#0a1628', letterSpacing: '-0.025em' }}>
                Quality isn't just about price.
              </h3>
              <p className="text-base text-gray-500 max-w-2xl mx-auto">
                Three more reasons OpenbooQ installs are held to a higher standard than the industry average:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {[
                { Icon: Award, title: 'Manufacturer-Certified Crews', body: "Every install crew is certified by the shingle manufacturer they're installing — GAF, Owens Corning, TAMKO, or Atlas. Not random subcontractors. Not lowest-bid labor." },
                { Icon: Video, title: 'Body-Cam on Every Install', body: 'Crew leads wear body cams from tear-off through cleanup. Footage available to you on request. Accountability built into the process — not promised, recorded.' },
                { Icon: Shield, title: 'Two Warranty Layers', body: '5-year OpenbooQ workmanship warranty on the install. Manufacturer material warranty on the shingles — up to lifetime depending on product. Both register automatically.' },
              ].map(({ Icon, title, body }, i) => (
                <motion.article
                  key={title}
                  variants={cardStaggerV}
                  custom={i}
                  whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                  className="rounded-2xl p-6 lg:p-7 bg-white cursor-default"
                  style={{
                    border: '1px solid #E5EBF0',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(15, 23, 42, 0.06)',
                  }}
                >
                  <motion.div
                    variants={iconBadgeV}
                    custom={i}
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                      boxShadow: '0 6px 16px rgba(7, 176, 233, 0.28)',
                    }}
                    aria-hidden="true"
                  >
                    <Icon size={22} strokeWidth={2.2} color="white" />
                  </motion.div>
                  <h4 className="text-lg lg:text-xl font-bold mb-2" style={{ color: '#0a1628' }}>
                    {title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ═══ $2M INSURANCE SPOTLIGHT ═══════════════════════════════ */}
        <motion.section
          className="py-16 lg:py-24"
          style={{ background: 'linear-gradient(180deg, #0a1628 0%, #142844 100%)' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={sectionV}
        >
          <div className="max-w-[1200px] mx-auto px-5 lg:px-8 text-white">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-10 mb-10 lg:mb-14">
              <motion.div
                variants={{
                  hidden: { opacity: 0, scale: 0.85 },
                  visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
                }}
                className="flex-shrink-0 font-black leading-none text-center"
                style={{
                  fontSize: 'clamp(80px, 12vw, 140px)',
                  background: 'linear-gradient(180deg, #6cc7ff 0%, #07B0E9 50%, #0a6eb8 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.04em',
                  filter: 'drop-shadow(0 8px 32px rgba(7,176,233,0.35))',
                }}
              >
                $2M
              </motion.div>
              <div className="flex-1 text-center md:text-left">
                <motion.div variants={eyebrowV} className="mb-4 flex md:justify-start justify-center">
                  <EyebrowPill variant="dark">General Liability Coverage</EyebrowPill>
                </motion.div>
                <motion.h2
                  variants={headlineV}
                  className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] tracking-tight mb-4"
                  style={{
                    fontFamily: "'Poppins', 'Montserrat', sans-serif",
                    letterSpacing: '-0.025em',
                  }}
                >
                  $2 million in coverage. <span style={{ color: '#07B0E9' }}>Per job.</span> Not per year.
                </motion.h2>
                <p className="text-base lg:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.82)' }}>
                  Many residential roofers carry $1 million in general liability coverage at the company level. <strong>OpenbooQ carries $2 million on every single project</strong> — your job, specifically, is covered for double the typical limit from the moment materials hit your property through final cleanup.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-8">
              {[
                { Icon: HomeIcon, title: 'Property Damage', body: "If anything is damaged to your home, landscaping, or property during install, our policy covers it up to $2M — never your homeowner's insurance." },
                { Icon: Users, title: 'Crew Injury', body: "If a crew member is injured on your property, our coverage takes care of it. Your homeowner's policy stays untouched — and your name stays off any claim." },
                { Icon: Box, title: 'Materials & Equipment', body: 'From the moment materials are dropped at your home through tear-off, install, and final cleanup — everything OpenbooQ brings is insured. Theft, weather, accidents.' },
              ].map(({ Icon, title, body }, i) => (
                <motion.article
                  key={title}
                  variants={cardStaggerV}
                  custom={i}
                  whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                  className="rounded-2xl p-6 cursor-default"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(108, 199, 255, 0.25)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                  }}
                >
                  <motion.div
                    variants={iconBadgeV}
                    custom={i}
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                      boxShadow: '0 6px 16px rgba(7, 176, 233, 0.35)',
                    }}
                    aria-hidden="true"
                  >
                    <Icon size={20} strokeWidth={2.2} color="white" />
                  </motion.div>
                  <h4 className="text-lg font-bold mb-2 text-white">{title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>{body}</p>
                </motion.article>
              ))}
            </div>

            <motion.p
              variants={subheadV}
              className="text-center text-base max-w-3xl mx-auto"
              style={{ color: 'rgba(255,255,255,0.82)' }}
            >
              $2M GL coverage is <strong>per job. Per project. Per roof.</strong> Not a company-wide policy that gets divided up across hundreds of installs — the full $2M is dedicated to <em>your</em> home from materials drop to final sign-off.
            </motion.p>
          </div>
        </motion.section>

        {/* ═══ QUALITY CLOSER / SIGNATURE ═══════════════════════════ */}
        <motion.section
          className="py-12 lg:py-16 bg-white"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={sectionV}
        >
          <div className="max-w-[1080px] mx-auto px-5 lg:px-8 text-center">
            <motion.h3
              variants={headlineV}
              className="text-2xl sm:text-3xl lg:text-4xl font-black mb-3 leading-tight"
              style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
            >
              30–50% less. Same shingles. Same warranties. <span style={{ color: '#07B0E9' }}>Better insurance.</span> Zero risk to you.
            </motion.h3>
            <motion.p variants={subheadV} className="text-sm italic text-gray-500 tracking-wide">
              An open book, down to the last dollar.
            </motion.p>
          </div>
        </motion.section>

        {/* ═══ FAQ ═══════════════════════════════════════════════════ */}
        <motion.section
          id="faq"
          className="py-16 lg:py-24"
          style={{ background: '#F8FAFC' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={sectionV}
        >
          <div className="max-w-[1080px] mx-auto px-5 lg:px-8">
            <SectionHeader
              eyebrow="The Real Questions"
              headline="The six questions buyers actually ask."
              subhead={<>Not "what are your hours?" — the real ones. The skeptical ones. The ones we hear at the kitchen table.</>}
            />

            <div className="space-y-4">
              {FAQ_ITEMS.map((item, i) => (
                <motion.details
                  key={item.q}
                  variants={cardStaggerV}
                  custom={i}
                  whileHover={{ y: -2, transition: { duration: 0.2, ease: 'easeOut' } }}
                  className="group rounded-2xl bg-white overflow-hidden hover:border-[#07B0E9] transition-colors"
                  style={{
                    border: '1.5px solid rgba(7, 176, 233, 0.30)',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  <summary
                    className="flex items-center justify-between gap-4 p-5 lg:p-6 cursor-pointer list-none"
                  >
                    <span
                      className="text-xs font-bold uppercase tracking-widest flex-shrink-0"
                      style={{ color: '#07B0E9' }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-bold text-base lg:text-lg leading-snug flex-1" style={{ color: '#000150' }}>
                      {item.q}
                    </h3>
                    <span
                      className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: '#07B0E9', boxShadow: '0 2px 4px rgba(7, 176, 233, 0.35)' }}
                      aria-hidden="true"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" className="group-open:hidden">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" className="hidden group-open:block">
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-5 lg:px-6 pb-5 lg:pb-6 text-sm lg:text-[15px] text-gray-600 leading-relaxed space-y-3">
                    {item.a}
                  </div>
                </motion.details>
              ))}
            </div>
          </div>
        </motion.section>

        {/* ═══ STILL HAVE QUESTIONS — Chat vs Phone ════════════════ */}
        <motion.section
          className="bg-white py-16 lg:py-24"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={sectionV}
        >
          <div className="max-w-[1080px] mx-auto px-5 lg:px-8">
            <div className="text-center mb-10 lg:mb-12">
              <motion.h3
                variants={headlineV}
                className="text-2xl sm:text-3xl lg:text-4xl font-black mb-3"
                style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
              >
                Still have questions?
              </motion.h3>
              <motion.p variants={subheadV} className="text-base text-gray-500">
                Two ways to get answers. We're honest about which one is which.
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 items-stretch">
              {/* Chat card — flex column so the CTA can be pushed to the
                  bottom with mt-auto, keeping both cards' buttons
                  aligned at the same vertical position regardless of
                  description length. */}
              <motion.article
                variants={cardStaggerV}
                custom={0}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                className="rounded-2xl p-6 lg:p-8 flex flex-col cursor-default"
                style={{
                  background: '#f7f9fc',
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                      boxShadow: '0 6px 16px rgba(7, 176, 233, 0.32)',
                    }}
                    aria-hidden="true"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#0a6eb8' }}>Fast Answers</div>
                    <div className="text-xl font-black" style={{ color: '#0a1628', letterSpacing: '-0.01em' }}>Chat with our assistant</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  For the most common questions — pricing scope, materials, scheduling, what's included — our chat assistant handles them <strong style={{ color: '#0a1628', fontWeight: 800 }}>instantly, 24/7.</strong> No phone tag. No callback windows.
                </p>
                <div
                  className="rounded-xl p-3.5 text-xs leading-relaxed mb-6 flex gap-2"
                  style={{ background: '#e8f5fd', color: '#0a6eb8' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <span>Yes, it's an AI assistant. We're honest about that. For complex or situational questions, the phone is better.</span>
                </div>
                {/* mt-auto pushes the button to the bottom of the flex
                    column → button aligns with the matching button in
                    the other card regardless of description wrap.
                    Clicking opens the sticky Chatbase widget that's
                    embedded globally via index.html (bottom-right). */}
                <button
                  type="button"
                  onClick={handleOpenChat}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all duration-200 hover:-translate-y-0.5 self-start mt-auto"
                  style={{
                    background: '#0a1628',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(10, 22, 40, 0.22)',
                  }}
                >
                  Open chat
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </motion.article>

              {/* Phone card — same flex-column treatment so the CTAs
                  in both cards bottom-align. */}
              <motion.article
                variants={cardStaggerV}
                custom={1}
                whileHover={{ y: -4, transition: { duration: 0.25, ease: 'easeOut' } }}
                className="rounded-2xl p-6 lg:p-8 flex flex-col cursor-default"
                style={{
                  background: '#f7f9fc',
                  border: '1px solid #e5ebf0',
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06)',
                }}
              >
                <div className="flex items-start gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.32)',
                    }}
                    aria-hidden="true"
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#047857' }}>Real Conversation</div>
                    <div className="text-xl font-black" style={{ color: '#0a1628', letterSpacing: '-0.01em' }}>Call a real roofer</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed mb-5">
                  For everything else — specific roof situations, insurance questions, custom material selections, anything nuanced — <strong style={{ color: '#0a1628', fontWeight: 800 }}>pick up the phone.</strong> An actual roofer picks up, not a call center, not a salesperson.
                </p>
                <div
                  className="rounded-xl p-3.5 text-xs leading-relaxed mb-6 flex gap-2"
                  style={{ background: '#d1fae5', color: '#047857' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Real human, not outsourced. The person you reach has actually been on a roof.</span>
                </div>
                <a
                  href="tel:9379108791"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all duration-200 hover:-translate-y-0.5 self-start mt-auto"
                  style={{
                    background: '#047857',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(4, 120, 87, 0.30)',
                  }}
                >
                  Call 937-910-8791
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </a>
              </motion.article>
            </div>
          </div>
        </motion.section>

        {/* ═══ FINAL CTA ═════════════════════════════════════════════ */}
        <motion.section
          id="cta"
          className="py-16 lg:py-24 text-center"
          style={{ background: 'linear-gradient(180deg, #0a1628 0%, #07B0E9 200%)' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionV}
        >
          <div className="max-w-[1080px] mx-auto px-5 lg:px-8 text-white">
            <motion.h2
              variants={headlineV}
              className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
            >
              Your roof — in <span style={{ color: '#6cc7ff' }}>7 days.</span>
            </motion.h2>
            <motion.p variants={subheadV} className="text-base lg:text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.78)' }}>
              $0 down. ~30 minutes from address to a full quote. Cancel free in the first 72 hours.
            </motion.p>

            <motion.a
              variants={ctaV}
              href={PORTAL_BASE_URL}
              onClick={handleCtaClick}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl font-bold text-base transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #07B0E9 0%, #0596C7 100%)',
                color: 'white',
                boxShadow: '0 8px 24px rgba(7, 176, 233, 0.5)',
              }}
            >
              Get my price
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </motion.a>

            <div
              className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mt-8"
              style={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <span>✓ Free, no-obligation quote</span>
              <span>✓ ~30 minute quote turnaround</span>
              <span>✓ Real roofers, never salespeople</span>
              <span>✓ 72-hour free cancellation after signing</span>
            </div>

            <p className="text-sm italic mt-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
              An open book, down to the last dollar.
            </p>
          </div>
        </motion.section>
      </main>

      <Footer />
    </>
  );
};

// ─── Phase header (colored gradient strip with left border) ──────────
//
// 3 variants — blue (you), purple (us / OpenbooQ), green (final / install
// day). Mockup-faithful — gradient bg with a 4px left border matching the
// variant's accent color. Scroll-fades from the left on viewport entry.
const PhaseHeader = ({ variant, eyebrow, title, meta }) => {
  const variants = {
    you: { bg: 'linear-gradient(135deg, #f0f9ff 0%, #e8f5fd 100%)', border: '#07B0E9', eyebrowColor: '#0a6eb8' },
    us: { bg: 'linear-gradient(135deg, #ede9fe 0%, #f3e8ff 100%)', border: '#7c3aed', eyebrowColor: '#7c3aed' },
    final: { bg: 'linear-gradient(135deg, #d1fae5 0%, #ecfdf5 100%)', border: '#10b981', eyebrowColor: '#047857' },
  };
  const v = variants[variant] || variants.you;
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      variants={phaseHeaderV}
      className="rounded-r-2xl py-5 px-6 lg:py-6 lg:px-8"
      style={{
        background: v.bg,
        borderLeft: `4px solid ${v.border}`,
      }}
    >
      <p
        className="text-xs font-bold uppercase tracking-[0.14em] mb-2"
        style={{ color: v.eyebrowColor }}
      >
        {eyebrow}
      </p>
      <h2
        className="text-xl sm:text-2xl lg:text-[28px] font-black leading-[1.2] tracking-tight"
        style={{
          color: '#0a1628',
          fontFamily: "'Poppins', 'Montserrat', sans-serif",
          letterSpacing: '-0.025em',
        }}
      >
        {title}
        {meta && (
          <span className="text-sm lg:text-base font-semibold ml-3 align-middle" style={{ color: '#6B7A8E', letterSpacing: '0', textTransform: 'none' }}>
            {meta}
          </span>
        )}
      </h2>
    </motion.div>
  );
};

// ─── Step block — mockup-faithful format ────────────────────────────
//
// Header layout: square 14px-radius cyan-gradient number tile on the
// LEFT, title block on the RIGHT (h3 + badge row underneath). Step
// content sits flat below — no nested card wrapper around it; each
// step IS the card (the surrounding section bg = white).
//
// Step badges (mockup): "YOU" → blue-light + blue-deep, "OPENBOOQ" →
// purple-soft + purple, "INSTALL DAY"/"$0 DOWN" → green-soft + green-deep.
const BADGE_STYLES = {
  You: { bg: '#e8f5fd', color: '#0a6eb8' },
  OpenbooQ: { bg: '#ede9fe', color: '#7c3aed' },
  'Install Day': { bg: '#d1fae5', color: '#047857' },
  '$0 Down': { bg: '#d1fae5', color: '#047857' },
};

const ProcessStep = ({ step }) => {
  const badgeStyle = BADGE_STYLES[step.badge] || BADGE_STYLES.You;
  return (
    <motion.article
      id={`step-${step.n}`}
      className="scroll-mt-32"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={stepV}
    >
      {/* Header: square cyan number + title/badge */}
      <div className="flex items-start gap-4 lg:gap-5 mb-5">
        <div
          className="flex-shrink-0 flex items-center justify-center font-black"
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
            color: 'white',
            fontSize: '20px',
            letterSpacing: '-0.02em',
            boxShadow: '0 6px 18px rgba(7, 176, 233, 0.32)',
          }}
        >
          {step.n}
        </div>
        <div className="flex-1 min-w-0 pt-1">
          <h3
            className="text-xl sm:text-2xl lg:text-[30px] font-black leading-[1.15] mb-2"
            style={{
              color: '#0a1628',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.025em',
            }}
          >
            {step.title}
          </h3>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span
              className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.1em]"
              style={{ background: badgeStyle.bg, color: badgeStyle.color }}
            >
              {step.badge}
            </span>
            <span className="font-semibold" style={{ color: '#6B7A8E' }}>{step.time}</span>
          </div>
        </div>
      </div>

      {/* Lead paragraph */}
      <p className="text-base lg:text-[17px] text-gray-600 leading-relaxed mb-5">{step.lead}</p>

      {/* Bullet list (steps 1, 3, 4) */}
      {step.bullets && (
        <ul className="space-y-3 mb-5">
          {step.bullets.map((b, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <span className="flex-shrink-0 mt-1"><CheckIcon /></span>
              <span className="text-sm lg:text-[15px] text-gray-600 leading-relaxed">{b}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Sub-grid (steps 2, 5, 6) — 2×2 cards with cyan icon.
          Hover: lift + cyan border + cyan-tinted shadow brighten. */}
      {step.subGrid && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-5">
          {step.subGrid.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="rounded-xl p-4 lg:p-5 bg-white transition-all duration-250 hover:-translate-y-1 hover:border-[#07B0E9]/40 hover:shadow-[0_8px_20px_rgba(7,176,233,0.12)] cursor-default"
              style={{
                border: '1px solid #E5EBF0',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #07B0E9 0%, #0a6eb8 100%)',
                    boxShadow: '0 4px 10px rgba(7, 176, 233, 0.28)',
                  }}
                  aria-hidden="true"
                >
                  {Icon ? <Icon size={18} strokeWidth={2.2} color="white" /> : null}
                </div>
                <h5 className="text-sm lg:text-base font-bold" style={{ color: '#0a1628' }}>{title}</h5>
              </div>
              <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      )}

      {/* Step 7 — DRAMATIC EMPHASIS BLOCK + $0 payment timeline.
          Emphasis block: dark-navy gradient with a giant cyan "$0"
          graphic on the left, "HEAVY EMPHASIS · BY DESIGN" eyebrow on
          the right, and the "you owe nothing." headline in cyan glow.
          Timeline below: 4 cards — $0 amounts in GREEN (no payment),
          final PAY card in cyan with cyan border (highlighted). */}
      {step.paymentTimeline && (
        <>
          <div
            className="rounded-2xl p-6 lg:p-10 mb-5 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0a1628 0%, #142844 100%)',
              boxShadow: '0 20px 50px rgba(10, 22, 40, 0.25)',
            }}
          >
            {/* Cyan ambient glow behind the $0 */}
            <div
              className="absolute pointer-events-none"
              aria-hidden="true"
              style={{
                top: '50%',
                left: '15%',
                transform: 'translate(-50%, -50%)',
                width: '320px',
                height: '320px',
                background: 'radial-gradient(circle, rgba(7,176,233,0.30) 0%, transparent 70%)',
                filter: 'blur(40px)',
              }}
            />
            <div className="relative flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-10">
              <div
                className="flex-shrink-0 font-black leading-none text-center"
                style={{
                  fontSize: 'clamp(96px, 14vw, 180px)',
                  background: 'linear-gradient(180deg, #6cc7ff 0%, #07B0E9 50%, #0a6eb8 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.04em',
                  filter: 'drop-shadow(0 8px 32px rgba(7,176,233,0.45))',
                }}
              >
                $0
              </div>
              <div className="flex-1 text-center md:text-left text-white">
                <div className="mb-3 flex md:justify-start justify-center">
                  <EyebrowPill variant="dark">Heavy Emphasis · By Design</EyebrowPill>
                </div>
                <h4
                  className="font-black mb-3 leading-[1.15] tracking-tight"
                  style={{
                    fontSize: 'clamp(22px, 3vw, 32px)',
                    fontFamily: "'Poppins', 'Montserrat', sans-serif",
                    letterSpacing: '-0.025em',
                  }}
                >
                  From the day you enter your address through install day,{' '}
                  <span
                    style={{
                      color: '#6cc7ff',
                      textShadow: '0 0 20px rgba(7, 176, 233, 0.55)',
                    }}
                  >
                    you owe nothing.
                  </span>
                </h4>
                <p className="text-sm lg:text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>
                  OpenbooQ pays our suppliers, schedules the crew, and pulls the permits — all before you've moved a dollar. The risk is on us, not on you. Payment becomes due only after install is complete and you've approved the work.
                </p>
              </div>
            </div>
          </div>

          {/* 4-card payment timeline */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-5">
            {[
              { amount: '$0', label: 'At Signing', desc: 'No payment. No deposit. No card on file.', final: false },
              { amount: '$0', label: 'Materials Ordered', desc: 'We pay our suppliers. Not you.', final: false },
              { amount: '$0', label: 'Install Day', desc: 'Crew arrives. Still nothing due.', final: false },
              { amount: 'PAY', label: 'At Completion', desc: 'QC sign-off triggers your final invoice.', final: true },
            ].map((tl) => (
              <div
                key={tl.label}
                className="rounded-xl p-4 text-center"
                style={{
                  background: tl.final ? 'rgba(7, 176, 233, 0.10)' : '#F8FAFC',
                  border: tl.final
                    ? '2px solid rgba(7, 176, 233, 0.55)'
                    : '1px solid #E5EBF0',
                }}
              >
                <div
                  className="text-2xl lg:text-3xl font-black mb-1 leading-none"
                  style={{
                    color: tl.final ? '#07B0E9' : '#10B981',
                    letterSpacing: '-0.03em',
                  }}
                >
                  {tl.amount}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-2 mt-2" style={{ color: tl.final ? '#07B0E9' : '#6B7A8E' }}>
                  {tl.label}
                </div>
                <p className="text-xs text-gray-600 leading-snug">{tl.desc}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Trust callout — left blue border + light blue gradient */}
      {step.trust && (
        <div
          className="rounded-r-xl py-4 px-5 lg:py-5 lg:px-6 text-sm lg:text-[15px] leading-relaxed"
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e8f5fd 100%)',
            borderLeft: '3px solid #07B0E9',
            color: '#0a1628',
          }}
        >
          {step.trust}
        </div>
      )}
    </motion.article>
  );
};

export default HowItWorks;
